import React, { useCallback, useEffect, useState } from 'react'
import clsx from 'clsx'
import moment from 'moment'
import { isNil } from 'ramda'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import momentTimezone from 'moment-timezone'
import { getLocale } from 'app/translations/intl-context'
import { Typography, makeStyles } from '@material-ui/core'

import { changeTimezone } from 'libs/dateUtils'
import { getDateFromNow, timeFormat, dateFormat } from 'commons/date'
import {
  POLL_OPEN,
  POLL_TPYE_MULTI,
  POLL_TPYE_SINGLE,
} from 'app/constants/messageDetail'

const useStyles = makeStyles({
  list: {
    '&:first-child': {
      '& .bullet': {
        display: 'none',
      },
    },
  },
})

const SimplePollDetails = ({ data }) => {
  const { type, status, endDate, endDateTimeZone, isAnonymous } = data

  const classes = useStyles()
  const localLang = getLocale()
  const { formatMessage } = useIntl()

  const [state, setState] = useState({})

  const displayEndDate = useCallback(
    (status, endDate, timeZone = momentTimezone.tz.guess()) => {
      let message = null
      const isOpen = status === POLL_OPEN
      const isEndDate = !isNil(endDate)

      if (isOpen && isEndDate)
        message = getDateFromNow(moment(endDate), localLang, true)
      else if (isOpen && !isEndDate) message = null
      else if (!isOpen && isEndDate) {
        const date = changeTimezone(endDate, timeZone).format(dateFormat)
        const time = changeTimezone(endDate, timeZone).format(timeFormat)
        const offsets = momentTimezone.tz(timeZone).format('Z')

        message = formatMessage(
          { id: 'label.ends.on' },
          {
            date: formatMessage(
              { id: 'label.date.utc' },
              { date, time, offsets },
            ),
          },
        )
      } else message = null

      return message
    },
    [formatMessage, localLang],
  )

  const displayType = useCallback(
    (type) => {
      let message = null

      if (type === POLL_TPYE_SINGLE) message = null
      else if (type === POLL_TPYE_MULTI)
        message = formatMessage({ id: 'poll.type.multi.vote' })
      else message = null

      return message
    },
    [formatMessage],
  )

  const displayAnonymous = useCallback(
    (isAnonymous) => {
      let message = null

      if (isAnonymous) message = formatMessage({ id: 'poll.anonymous.vote' })
      else message = null

      return message
    },
    [formatMessage],
  )

  const handleDetail = useCallback(
    async (status, endDate, timeZone, type, isAnonymous) => {
      setState({
        date: await displayEndDate(status, endDate, timeZone),
        type: await displayType(type),
        anonymous: await displayAnonymous(isAnonymous),
      })
    },
    [displayType, displayEndDate, displayAnonymous],
  )

  useEffect(() => {
    handleDetail(status, endDate, endDateTimeZone, type, isAnonymous)
  }, [endDate, endDateTimeZone, handleDetail, isAnonymous, status, type])

  return (
    <div className="flex text-12 text-grey-300">
      {Object.keys(state).map((list, index) => (
        <div key={index}>
          {isNil(state[list]) ? null : (
            <div className={clsx(classes.list, 'pr-3')}>
              {index > 0 && (
                <Typography component="span" className="mr-3">
                  •
                </Typography>
              )}
              <Typography component="span" className="text">
                {state[list]}
              </Typography>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

SimplePollDetails.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.number,
    type: PropTypes.string,
    status: PropTypes.string,
    endDate: PropTypes.string,
    isAnonymous: PropTypes.bool,
    optionCount: PropTypes.number,
    endDateTimeZone: PropTypes.string,
  }),
}

export default SimplePollDetails
